import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon, MagnifyingGlassIcon, DocumentTextIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';
import { transcriptApi } from '../../../api/transcriptApi';
import { eventsApi } from '../../../api/eventsApi';

const SearchPalette = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const resultsRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
      setQuery('');
      setResults([]);
      setSelectedIndex(0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' && results[selectedIndex]) {
        e.preventDefault();
        handleSelectResult(results[selectedIndex]);
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, results, selectedIndex, onClose]);

  useEffect(() => {
    if (query.length < 2) {
      setResults([]);
      return;
    }

    const search = async () => {
      setLoading(true);
      try {
        // Search sessions
        const sessionsResponse = await transcriptApi.getSessions({ 
          search: query, 
          per_page: 5 
        });
        const sessions = Array.isArray(sessionsResponse?.sessions) 
          ? sessionsResponse.sessions 
          : Array.isArray(sessionsResponse?.results) 
          ? sessionsResponse.results 
          : [];

        // Search events
        const eventsResponse = await eventsApi.getAll({ limit: 5 });
        const events = (eventsResponse?.events || []).filter(e =>
          e.title?.toLowerCase().includes(query.toLowerCase())
        );

        const searchResults = [
          ...sessions.slice(0, 5).map(s => ({
            type: 'session',
            id: s.id,
            title: s.title || 'Untitled Session',
            description: s.description || s.event_title || '',
            url: `/sessions/${s.id}`,
            icon: DocumentTextIcon
          })),
          ...events.slice(0, 3).map(e => ({
            type: 'event',
            id: e.id,
            title: e.title || 'Untitled Event',
            description: e.description || '',
            url: `/events/${e.id}`,
            icon: CalendarDaysIcon
          }))
        ];

        setResults(searchResults);
        setSelectedIndex(0);
      } catch (error) {
        console.error('Search error:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(search, 300);
    return () => clearTimeout(timeoutId);
  }, [query]);

  const handleSelectResult = (result) => {
    navigate(result.url);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[20vh] bg-black bg-opacity-50" onClick={onClose}>
      <div
        className="bg-white"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search Input */}
        <div className="flex items-center gap-3 p-4 border-b border-gray-200">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search sessions, events..."
            className="flex-1 bg-transparent border-none outline-none text-gray-900"
          />
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Results */}
        <div className="overflow-y-auto flex-1" ref={resultsRef}>
          {loading && (
            <div className="p-4 text-center text-gray-500">
              Searching...
            </div>
          )}
          {!loading && query.length < 2 && (
            <div className="p-4 text-center text-gray-500">
              Type at least 2 characters to search
            </div>
          )}
          {!loading && query.length >= 2 && results.length === 0 && (
            <div className="p-4 text-center text-gray-500">
              No results found
            </div>
          )}
          {!loading && results.length > 0 && (
            <div className="py-2">
              {results.map((result, index) => {
                const Icon = result.icon;
                return (
                  <button
                    key={`${result.type}-${result.id}`}
                    onClick={() => handleSelectResult(result)}
                    className={`w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-gray-100
                      index === selectedIndex ? 'bg-gray-100' : ''
                    }`}
                  >
                    <Icon className="h-5 w-5 text-gray-400" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900">
                        {result.title}
                      </div>
                      {result.description && (
                        <div className="text-xs text-gray-500">
                          {result.description}
                        </div>
                      )}
                    </div>
                    <span className="text-xs text-gray-400 uppercase">
                      {result.type}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-gray-200">
          <div className="flex items-center gap-4">
            <span>↑↓ Navigate</span>
            <span>Enter Select</span>
            <span>Esc Close</span>
          </div>
          <span>Ctrl+K to open</span>
        </div>
      </div>
    </div>
  );
};

export default SearchPalette;
